/*
 * Select Anime - cart shipping estimate  <sa-shipping-estimate>
 *
 * Why a custom element instead of an inline <script>:
 *   The cart drawer is re-rendered by assigning innerHTML / replaceWith:
 *     - assets/cart.js  CartDrawerItems.getSectionsToRender() -> `.drawer__inner`
 *       is replaced with `elementToReplace.innerHTML = ...` on every quantity change.
 *     - assets/cart.js  CartItems.onCartUpdate() -> `.cart-drawer__footer` is
 *       replaced with replaceWith(node) parsed by DOMParser.
 *     - assets/cart-drawer.js  CartDrawer.renderContents() -> `#CartDrawer`
 *       innerHTML is replaced when a product is added from a product page.
 *   Scripts inserted through innerHTML never execute again and their listeners are
 *   gone, so an inline IIFE dies on the first re-render. Custom elements are
 *   upgraded when the parsed node is inserted into the live document, so
 *   connectedCallback runs again on every re-render. This is the same mechanism
 *   Dawn relies on for <cart-drawer-items>, <quantity-input> and <cart-note>.
 *
 * Endpoints (Shopify Ajax Cart API, verified 2026-08-20 on shopify.dev):
 *   POST /cart/prepare_shipping_rates.json  starts the calculation
 *   GET  /cart/async_shipping_rates.json    returns null until ready, then rates
 *   GET  /cart/shipping_rates.json          legacy single shot, documented as
 *                                           "subject to throttling", fallback only
 *
 * Staleness: the element subscribes to PUB_SUB_EVENTS.cartUpdate and clears the
 * displayed rate, so a rate calculated for an older cart is never left on screen.
 */
(function () {
  'use strict';

  if (typeof window.customElements === 'undefined') return;
  if (window.customElements.get('sa-shipping-estimate')) return;

  var TEXT = {
    working: 'Estimating\u2026',
    heading: 'Estimated shipping for this cart',
    note: 'This is the rate for the items currently in your cart. It is confirmed at checkout.',
    unavailable: 'Shipping is calculated at checkout by weight and destination.',
    stale: 'Your cart changed. Select Estimate to update the rate.',
    customsUs: 'No duties or taxes are billed on delivery for U.S. orders (DDP).',
    customsIntl:
      'Import duties or taxes, if any, are billed by your country and are the buyer\u2019s responsibility.',
  };

  var POLL_INTERVAL = 600;
  var POLL_ATTEMPTS = 8;
  var LEGACY_INTERVAL = 1000;
  var LEGACY_ATTEMPTS = 3;
  var CHANGE_DEBOUNCE = 350;

  // Open/closed state survives a cart re-render, which builds a brand new
  // element from server markup. Module scope only: a fresh page load starts
  // collapsed, and no storage API is touched.
  var openState = {};

  function cartRoot() {
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return String(base).replace(/\/+$/, '');
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  class SAShippingEstimate extends HTMLElement {
    constructor() {
      super();
      this.requestToken = 0;
      this.listenersBound = false;
      this.countryParamStyle = null;
      this.debounceTimer = null;
    }

    connectedCallback() {
      this.details = this.querySelector('.sa-shipest__details');
      this.select = this.querySelector('.sa-shipest__select');
      this.button = this.querySelector('.sa-shipest__btn');
      this.resultEl = this.querySelector('.sa-shipest__result');
      this.customsEl = this.querySelector('.sa-shipest__customs');
      if (!this.select || !this.button || !this.resultEl) return;

      if (!this.listenersBound) {
        this.listenersBound = true;
        this.button.addEventListener('click', this.onEstimateClick.bind(this));
        this.select.addEventListener('change', this.onCountryChange.bind(this));
        if (this.details) this.details.addEventListener('toggle', this.onToggle.bind(this));
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      // Re-open after a re-render, but without estimating: the cart just changed,
      // so the customer decides when to ask for a new rate.
      this.stateKey = this.details ? this.details.id : null;
      if (this.details && this.stateKey && openState[this.stateKey] && !this.details.open) {
        this.restoringOpen = true;
        this.details.open = true;
      }

      this.renderCustoms();
    }

    disconnectedCallback() {
      this.requestToken += 1;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = undefined;
      }
    }

    /* ------------------------------------------------------------------ events */

    onEstimateClick() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.estimate();
    }

    onCountryChange(event) {
      // The widget lives outside <cart-items> / <cart-drawer-items>, but stop the
      // event here anyway so a country change can never reach Dawn's cart change
      // handler if the markup is ever moved.
      if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
      this.renderCustoms();
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      var self = this;
      this.debounceTimer = setTimeout(function () {
        self.estimate();
      }, CHANGE_DEBOUNCE);
    }

    onToggle() {
      if (!this.details) return;
      if (this.stateKey) openState[this.stateKey] = !!this.details.open;
      if (this.restoringOpen) {
        this.restoringOpen = false;
        return;
      }
      if (!this.details.open) return;
      if (this.hasResult()) return;
      this.estimate();
    }

    onCartUpdate() {
      // Invalidate any in-flight request and drop a rate that belongs to an
      // older cart (assets/cart.js only swaps `.js-contents` on the cart page,
      // so this element can survive a quantity change with a stale amount).
      this.requestToken += 1;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      if (!this.resultEl) return;
      var hadResult = this.hasResult();
      this.resultEl.textContent = '';
      if (hadResult && this.details && this.details.open) this.showNote(TEXT.stale);
    }

    /* ------------------------------------------------------------------- rates */

    estimate() {
      var country = this.selectedCountry();
      if (!country) {
        this.showNote(TEXT.unavailable);
        return;
      }

      this.renderCustoms();
      this.requestToken += 1;
      var token = this.requestToken;
      var self = this;
      this.showNote(TEXT.working);

      // The Ajax API documents the country name ("Canada"). Some shops answer to
      // the ISO code as well, so whichever format works first is remembered and
      // tried first from then on.
      var order = this.countryParamStyle === 'code' ? ['code', 'name'] : ['name', 'code'];
      var first = country[order[0]];
      var second = country[order[1]];

      this.fetchRates(first)
        .then(function (rates) {
          if (rates !== null) {
            self.countryParamStyle = order[0];
            return rates;
          }
          if (!second || second === first) return null;
          return self.fetchRates(second).then(function (retry) {
            if (retry !== null) self.countryParamStyle = order[1];
            return retry;
          });
        })
        .then(function (rates) {
          if (token !== self.requestToken) return;
          if (rates && rates.length) {
            self.renderRates(rates);
          } else {
            self.showNote(TEXT.unavailable);
          }
        })
        .catch(function () {
          if (token === self.requestToken) self.showNote(TEXT.unavailable);
        });
    }

    // Resolves to an array of rates, [] when the destination has no rates, or
    // null when the request itself could not be completed.
    fetchRates(countryValue) {
      var query = 'shipping_address%5Bcountry%5D=' + encodeURIComponent(countryValue);
      var root = cartRoot();
      var self = this;

      return fetch(root + '/prepare_shipping_rates.json?' + query, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          if (!response.ok) return null;
          return self.pollAsyncRates(root, query, 0);
        })
        .catch(function () {
          return null;
        })
        .then(function (rates) {
          if (rates !== null) return rates;
          return self.legacyRates(root, query, 0);
        });
    }

    pollAsyncRates(root, query, attempt) {
      var self = this;
      return fetch(root + '/async_shipping_rates.json?' + query, {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          if (!response.ok) return null;
          return response.text();
        })
        .then(function (body) {
          if (body === null) return null;
          var data = null;
          try {
            data = JSON.parse(body);
          } catch (error) {
            return null;
          }
          // null means Shopify is still calculating.
          if (data && data.shipping_rates) return data.shipping_rates;
          if (attempt + 1 >= POLL_ATTEMPTS) return null;
          return wait(POLL_INTERVAL).then(function () {
            return self.pollAsyncRates(root, query, attempt + 1);
          });
        })
        .catch(function () {
          return null;
        });
    }

    legacyRates(root, query, attempt) {
      var self = this;
      return fetch(root + '/shipping_rates.json?' + query, {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          if (response.status === 202) {
            if (attempt + 1 >= LEGACY_ATTEMPTS) return null;
            return wait(LEGACY_INTERVAL).then(function () {
              return self.legacyRates(root, query, attempt + 1);
            });
          }
          if (!response.ok) return null;
          return response.json().then(function (data) {
            return (data && data.shipping_rates) || [];
          });
        })
        .catch(function () {
          return null;
        });
    }

    /* ----------------------------------------------------------------- display */

    hasResult() {
      return !!this.resultEl && this.resultEl.childNodes.length > 0;
    }

    showNote(message) {
      if (!this.resultEl) return;
      var note = document.createElement('p');
      note.className = 'sa-shipest__note';
      note.textContent = message;
      this.resultEl.textContent = '';
      this.resultEl.appendChild(note);
    }

    renderRates(rates) {
      if (!this.resultEl) return;
      var self = this;
      var fragment = document.createDocumentFragment();

      var heading = document.createElement('p');
      heading.className = 'sa-shipest__result-title';
      heading.textContent = TEXT.heading;
      fragment.appendChild(heading);

      var list = document.createElement('ul');
      list.className = 'sa-shipest__rates';
      list.setAttribute('role', 'list');

      rates.forEach(function (rate) {
        if (!rate) return;
        var item = document.createElement('li');
        item.className = 'sa-shipest__rate';

        var name = document.createElement('span');
        name.className = 'sa-shipest__rate-name';
        name.textContent = rate.presentment_name || rate.name || '';

        var price = document.createElement('span');
        price.className = 'sa-shipest__rate-price';
        price.textContent = self.formatMoney(rate.price);

        item.appendChild(name);
        item.appendChild(price);
        list.appendChild(item);
      });

      fragment.appendChild(list);

      var note = document.createElement('p');
      note.className = 'sa-shipest__note';
      note.textContent = TEXT.note;
      fragment.appendChild(note);

      this.resultEl.textContent = '';
      this.resultEl.appendChild(fragment);
    }

    renderCustoms() {
      if (!this.customsEl) return;
      var country = this.selectedCountry();
      this.customsEl.textContent = country && country.code === 'US' ? TEXT.customsUs : TEXT.customsIntl;
    }

    selectedCountry() {
      if (!this.select) return null;
      var option = this.select.options[this.select.selectedIndex];
      if (!option || !option.value) return null;
      return {
        code: option.value,
        name: option.getAttribute('data-name') || option.textContent.trim(),
      };
    }

    formatMoney(value) {
      var amount = parseFloat(value);
      var currency = this.getAttribute('data-currency') || 'USD';
      if (isNaN(amount)) return String(value == null ? '' : value);

      if (!this.moneyFormatter) {
        try {
          this.moneyFormatter = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency,
          });
        } catch (error) {
          this.moneyFormatter = null;
        }
      }

      if (this.moneyFormatter) return this.moneyFormatter.format(amount);
      return currency + ' ' + amount.toFixed(2);
    }
  }

  window.customElements.define('sa-shipping-estimate', SAShippingEstimate);
})();
